import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import { auth, googleProvider } from '../../../firebase';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { getAuthErrorMessage } from '../authFeedback';

const LoginCard = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    try {
      const rememberedEmail = window.localStorage.getItem('rememberedEmail');
      if (!rememberedEmail) return;
      setFormData((prev) => ({
        ...prev,
        email: rememberedEmail,
        rememberMe: true
      }));
    } catch {
      // Ignore localStorage read issues and continue with the default form state.
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors?.[name] || errors?.general) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
        general: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const normalizedEmail = formData.email.trim();
      await signInWithEmailAndPassword(auth, normalizedEmail, formData.password)
      try {
        if (formData?.rememberMe) {
          window.localStorage.setItem('rememberedEmail', normalizedEmail)
        } else {
          window.localStorage.removeItem('rememberedEmail')
        }
      } catch {
        // Ignore localStorage write issues; authentication already succeeded.
      }
      navigate('/', { replace: true })
    } catch (err) {
      setErrors({ general: getAuthErrorMessage(err, 'login') })
    } finally {
      setIsLoading(false)
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      await signInWithPopup(auth, googleProvider)
      navigate('/', { replace: true })
    } catch (err) {
      setErrors({ general: getAuthErrorMessage(err, 'google') })
    } finally {
      setIsLoading(false)
    }
  };

  const handleForgotPassword = () => {
    navigate('/password-reset');
  };

  return (
    <div className="flex w-full items-center">
      <Card
        variant="solid"
        hover="none"
        className="w-full rounded-none border-0 bg-white p-6 shadow-none sm:p-10 md:p-12"
      >
        <div className="mb-8">
          <p className="mb-2 text-xs font-medium uppercase tracking-[0.12em] text-[#656C78]">Account access</p>
          <h1 className="text-3xl font-semibold tracking-[-0.035em] text-[#17191D]">Sign in</h1>
          <p className="mt-3 text-sm leading-6 text-[#656C78]">
            Use your account to access your workspace. AU purchases are confirmed with the buyer’s 4-digit PIN.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errors?.general && (
            <div
              className="rounded-lg border border-destructive/25 bg-destructive/5 p-4"
              role="alert"
              aria-live="polite"
            >
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={16} className="mt-0.5 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-destructive">Sign-in could not be completed</p>
                  <p className="mt-1 text-sm text-destructive/90">{errors?.general}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label="Email Address"
              id="login-email"
              type="email"
              name="email"
              placeholder="you@yourcompany.com"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
              autoComplete="email"
              aria-invalid={Boolean(errors?.email)}
              aria-describedby={errors?.email ? 'login-email-error' : undefined}
              className="rounded-lg bg-white focus-visible:border-[#2457DB] focus-visible:ring-[#2457DB]/20"
            />
            {errors?.email && <span id="login-email-error" className="sr-only">{errors.email}</span>}

            <Input
              label="Password"
              id="login-password"
              type="password"
              name="password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
              autoComplete="current-password"
              aria-invalid={Boolean(errors?.password)}
              aria-describedby={errors?.password ? 'login-password-error' : undefined}
              className="rounded-lg bg-white focus-visible:border-[#2457DB] focus-visible:ring-[#2457DB]/20"
            />
            {errors?.password && <span id="login-password-error" className="sr-only">{errors.password}</span>}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#656C78]">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData?.rememberMe}
                onChange={handleInputChange}
                disabled={isLoading}
                className="h-4 w-4 rounded border-[#DCDFE5] text-[#2457DB] focus:ring-2 focus:ring-[#2457DB]/30 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
              Remember me
            </label>

            <button
              type="button"
              onClick={handleForgotPassword}
              className="rounded-sm text-sm font-medium text-[#2457DB] underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2457DB]"
              disabled={isLoading}
            >
              Forgot password?
            </button>
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              disabled={isLoading}
              className="rounded-lg bg-[#2457DB] text-white hover:bg-[#1E49BD]"
            >
              Sign In
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#DCDFE5]" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-3 text-xs text-[#656C78]">
                  or
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={handleGoogleLogin}
              disabled={isLoading}
              iconName="Chrome"
              iconPosition="left"
              className="rounded-lg border-[#DCDFE5] bg-white text-[#17191D] hover:bg-[#F5F6F8]"
            >
              Continue with Google
            </Button>
          </div>

          <div className="border-t border-[#DCDFE5] pt-5">
            <p className="text-xs leading-5 text-[#656C78]">
              Need access? Contact your administrator.
            </p>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default LoginCard;
